import React, {
  useRef,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import {
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import _ from 'underscore';

import { APP } from '../../../config';
import Image from '../../shared/Image';

import SCROLL_LEFT_CHEVRON from '../../../../images/icons/icon-scroll-left-chevron.svg';
import SCROLL_RIGHT_CHEVRON from '../../../../images/icons/icon-scroll-right-chevron.svg';

export default function CardCarousel({
  children,
  onCarouselScroll,
  onScrollViewLayout,
  controlsDisabled,
  pagingEnabled,
  viewableWidthOffset,
  resetOnContentResize,
  snapToOffsets,
  showsScrollIndicator,
  testID,
}) {
  /* Bunch o' layout logic here. The gist of it is, show left/right scroll buttons
   * if the slider can be scrolled in that direction. We don't do that in-app
   */

  const sliderRef = useRef(null);
  const [leftScrollable, setLeftScrollable] = useState(false);
  const [rightScrollable, setRightScrollable] = useState(true);

  const [currentScrollPos, setCurrentScrollPos] = useState(0);
  const [viewableWidth, setViewableWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);

  const onScroll = ({ nativeEvent }) => {
    const newViewableWidth = nativeEvent.layoutMeasurement.width;

    setViewableWidth(newViewableWidth);
    setCurrentScrollPos(nativeEvent.contentOffset.x);

    if (onCarouselScroll) {
      onCarouselScroll(nativeEvent);
    }
  };

  useEffect(() => {
    const controlsEnabled = !controlsDisabled && !APP;
    // should left scroll icon be showing?
    const nowLeftScrollable = controlsEnabled && currentScrollPos > 0;

    // should right scroll icon be showing?
    const atOrPastLastSnapOffset = snapToOffsets?.length
      ? Math.round(currentScrollPos) >= snapToOffsets[snapToOffsets.length - 1]
      : false;
    const nowRightScrollable = controlsEnabled
      && !atOrPastLastSnapOffset
      && currentScrollPos < contentWidth - viewableWidth;

    setLeftScrollable(nowLeftScrollable);
    setRightScrollable(nowRightScrollable);
  }, [
    controlsDisabled,
    currentScrollPos,
    snapToOffsets,
    viewableWidth,
    contentWidth,
  ]);

  const scrollLeft = () => {
    if (snapToOffsets?.length) {
      let index = _.findLastIndex(snapToOffsets, v => Math.round(currentScrollPos) > v);
      index = index < 0 ? 0 : index;
      sliderRef.current.scrollTo({
        x: snapToOffsets[index],
        y: 0,
        animated: true,
      });
      return;
    }

    sliderRef.current.scrollTo({
      x: currentScrollPos - (viewableWidth - viewableWidthOffset),
      y: 0,
      animated: true,
    });
  };

  const scrollRight = () => {
    if (snapToOffsets?.length) {
      let index = snapToOffsets.findIndex(v => v > Math.round(currentScrollPos));
      index = index < 0 ? snapToOffsets.length - 1 : index;
      sliderRef.current.scrollTo({
        x: snapToOffsets[index],
        y: 0,
        animated: true,
      });
      return;
    }

    sliderRef.current.scrollTo({
      x: currentScrollPos + (viewableWidth - viewableWidthOffset),
      y: 0,
      animated: true,
    });
  };

  const handleContentSizeChange = width => {
    if (resetOnContentResize) {
      sliderRef.current.scrollTo({ x: 0 });
    }
    setContentWidth(width);
  };

  return (
    <View testID={testID}>
      <ScrollView
        horizontal
        ref={sliderRef}
        onScroll={onScroll}
        onContentSizeChange={handleContentSizeChange}
        onLayout={e => {
          onScroll({
            nativeEvent: {
              layoutMeasurement: {
                width: e.nativeEvent.layout.width,
              },
              contentOffset: { x: e.nativeEvent.layout.x },
            },
          });
          onScrollViewLayout?.(e);
        }}
        scrollEventThrottle={200}
        pagingEnabled={pagingEnabled}
        decelerationRate="fast"
        showsHorizontalScrollIndicator={showsScrollIndicator}
      >
        { children }
      </ScrollView>

      { leftScrollable && (
        <Pressable
          testID="carousel-scroll-left"
          onPress={scrollLeft}
        >
          <Image source={SCROLL_LEFT_CHEVRON} />
        </Pressable>
      )}

      { rightScrollable && (
        <Pressable
          testID="carousel-scroll-right"
          onPress={scrollRight}
        >
          <Image source={SCROLL_RIGHT_CHEVRON} />
        </Pressable>
      )}
    </View>
  );
}

CardCarousel.propTypes = {
  children: PropTypes.node.isRequired,
  onCarouselScroll: PropTypes.func,
  onScrollViewLayout: PropTypes.func,
  /* Never show right/left scroll buttons */
  controlsDisabled: PropTypes.bool,
  /* See React Native ScrollView property of the same name */
  pagingEnabled: PropTypes.bool,
  /* See React Native ScrollView property of the same name. If set, overrides pagingEnabled */
  snapToOffsets: PropTypes.arrayOf(PropTypes.number),
  /* Offset the viewable width by this number */
  viewableWidthOffset: PropTypes.number,
  /* Whether to set scroll position to 0 when content size changes */
  resetOnContentResize: PropTypes.bool,
  /* See React Native ScrollView property "showsHorizontalScrollIndicator" */
  showsScrollIndicator: PropTypes.bool,
  /* Test ID for the Carousel */
  testID: PropTypes.string,
};

CardCarousel.defaultProps = {
  onCarouselScroll: null,
  onScrollViewLayout: null,
  controlsDisabled: false,
  pagingEnabled: false,
  viewableWidthOffset: 50,
  resetOnContentResize: false,
  snapToOffsets: null,
  showsScrollIndicator: true,
  testID: 'card-carousel-component',
};
